import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowLeft, Sun, Moon, Bell, Shield, Database, Info, LogOut, ChevronRight } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

export default function SettingsPage() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('zumo-theme') as 'dark' | 'light') || 'dark'
  )

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('zumo-theme', theme)
  }, [theme])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const items = [
    { icon: theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />, label: 'Theme', value: theme === 'dark' ? 'Dark' : 'Light', action: () => setTheme(t => t === 'dark' ? 'light' : 'dark') },
    { icon: <Bell size={18} />, label: 'Notifications', value: 'On', action: () => {} },
    { icon: <Shield size={18} />, label: 'Privacy', action: () => {} },
    { icon: <Database size={18} />, label: 'Storage & Data', action: () => {} },
    { icon: <Info size={18} />, label: 'About', value: 'v1.0.0', action: () => {} },
  ]

  return (
    <div className="settings-screen">
      <header className="settings-header">
        <button onClick={() => navigate(-1)} className="back-btn"><ArrowLeft size={20} /></button>
        <h2>Settings</h2>
        <div style={{ width: 36 }} />
      </header>

      <div className="profile-card">
        <div className="profile-avatar">{user?.username?.[0]?.toUpperCase()}</div>
        <div>
          <h3>{user?.display_name || user?.username}</h3>
          <p>{user?.email}</p>
          <span className="tier-badge">{user?.tier?.toUpperCase()}</span>
        </div>
      </div>

      <div className="settings-section">
        {items.map((item, i) => (
          <motion.div key={i} whileTap={{ scale: 0.98 }} className="settings-item" onClick={item.action}>
            <span className="settings-icon">{item.icon}</span>
            <span className="settings-label">{item.label}</span>
            {item.value && <span className="settings-value">{item.value}</span>}
            <ChevronRight size={16} color="var(--text-muted)" />
          </motion.div>
        ))}
      </div>

      <div className="settings-section">
        <motion.div whileTap={{ scale: 0.98 }} className="settings-item danger" onClick={handleLogout}>
          <span className="settings-icon"><LogOut size={18} /></span>
          <span className="settings-label">Log Out</span>
        </motion.div>
      </div>

      <style>{`
        .settings-screen { height: 100dvh; display: flex; flex-direction: column; background: var(--bg-primary); overflow-y: auto; }
        .settings-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: var(--bg-secondary); border-bottom: 1px solid var(--border); }
        .settings-header h2 { font-size: 18px; font-weight: 600; color: var(--text-primary); }
        .back-btn { background: none; border: none; color: var(--text-primary); cursor: pointer; display: flex; padding: 4px; }
        .profile-card { display: flex; align-items: center; gap: 16px; padding: 20px; background: var(--bg-secondary); border-bottom: 1px solid var(--border); }
        .profile-avatar { width: 56px; height: 56px; border-radius: 50%; background: var(--accent-dim); color: var(--accent); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 22px; }
        .profile-card h3 { font-size: 16px; font-weight: 600; color: var(--text-primary); }
        .profile-card p { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
        .tier-badge { display: inline-block; margin-top: 4px; padding: 2px 8px; background: var(--accent-dim); color: var(--accent); border-radius: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
        .settings-section { margin: 12px 0; }
        .settings-item { display: flex; align-items: center; gap: 14px; padding: 14px 20px; background: var(--bg-secondary); border-bottom: 1px solid var(--border); cursor: pointer; color: var(--text-primary); font-size: 14px; }
        .settings-item.danger { color: var(--danger); }
        .settings-icon { display: flex; align-items: center; color: var(--text-secondary); }
        .settings-label { flex: 1; }
        .settings-value { color: var(--text-muted); font-size: 13px; }
      `}</style>
    </div>
  )
}
